import { useEffect } from "react";
import { Link } from "react-router-dom";
import ArticleSchema from "@/components/ArticleSchema";
import Breadcrumbs from "@/components/Breadcrumbs";
import LanguageSwitchLink from "@/components/LanguageSwitchLink";
import PricingDisclaimer from "@/components/PricingDisclaimer";
import { SITE_ORIGIN, setPageMeta, setHreflang } from "@/lib/seo";

const EN_URL = "/blog/trailer-maintenance-tips";
const ES_URL = "/es/mantenimiento-de-remolques";

export default function MantenimientoDeRemolquesPage() {
  useEffect(() => {
    document.documentElement.lang = "es";
    setPageMeta(
      "Mantenimiento de Remolques: Consejos que te Ahorran Miles | Juan The Trailer Guy",
      "Guía práctica de mantenimiento de remolques y tráileres: rodamientos, llantas, frenos, luces y más. Evita reparaciones caras y tiempo muerto.",
      ES_URL,
    );
    const cleanup = setHreflang([
      { lang: "en-US", href: EN_URL },
      { lang: "es-US", href: ES_URL },
      { lang: "x-default", href: EN_URL },
    ]);
    return () => {
      document.documentElement.lang = "en";
      cleanup();
    };
  }, []);

  return (
    <main className="bg-background text-foreground">
      <ArticleSchema
        headline="Mantenimiento de Remolques: Consejos que te Ahorran Miles"
        description="Guía práctica de mantenimiento de remolques y tráileres: rodamientos, llantas, frenos, luces, chasis y más."
        url={`${SITE_ORIGIN}${ES_URL}`}
        image={`${SITE_ORIGIN}/images/jtg-logo-preview.png`}
      />
      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10">
          <Breadcrumbs
            variant="dark"
            className="mb-5"
            items={[{ name: "Inicio", href: "/" }, { name: "Guías Educativas" }, { name: "Mantenimiento de Remolques" }]}
          />
          <p className="text-sm uppercase tracking-[0.2em] text-red-400 font-semibold">Lo Básico del Remolque</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-black uppercase leading-tight">
            Mantenimiento de Remolques que te Puede Ahorrar Miles de Dólares
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-zinc-300">
            La mayoría de la gente no piensa en el mantenimiento hasta que algo se descompone — y para
            entonces ya está lidiando con tiempo muerto, trabajos perdidos y reparaciones caras. Un
            remolque bien cuidado te hace ganar dinero. Uno descuidado te lo quita. Aquí te explicamos
            cómo adelantarte al problema.
          </p>
          <div className="mt-6">
            <LanguageSwitchLink href={EN_URL} label="Read this guide in English" />
          </div>
        </div>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <PricingDisclaimer className="mb-8" />
          <h2 className="text-3xl font-black uppercase">1. Revisa tus Rodamientos (Bearings)</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Si no le das mantenimiento a tus rodamientos, se van a fallar. No es "tal vez" — se van a
            fallar.
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Revisa que tengan grasa con regularidad</li>
            <li>• Vuelve a engrasar los rodamientos cada 6 a 12 meses, según el uso</li>
            <li>• Pon atención a cualquier zumbido o ruido raro mientras jalas el tráiler</li>
          </ul>
          <p className="mt-6 text-muted-foreground">
            Un rodamiento malo arruina el cubo (hub) rapidísimo — y eso es un problema que te puede dejar
            varado en la carretera.
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">2. Llantas — No Escatimes Aquí</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Tus llantas cargan con todo. Peso + calor + condiciones del camino es igual a falla si las
            ignoras.
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Revisa la presión de aire antes de cada viaje</li>
            <li>• Checa el desgaste de la banda</li>
            <li>• Busca resequedad o cuarteaduras</li>
            <li>• Desgaste disparejo (puede ser señal de alineación o suspensión)</li>
          </ul>
          <p className="mt-6 font-semibold text-foreground">
            Una llanta reventada te puede costar mucho más que reemplazarlas a tiempo.
          </p>
        </div>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">3. Frenos — Asegúrate de que Funcionen Antes de Necesitarlos</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">Verifica que:</p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Los frenos agarren bien</li>
            <li>• El cableado esté en buen estado</li>
            <li>• No haya retraso ni frenado débil</li>
          </ul>
          <p className="mt-6 text-muted-foreground">
            Si tu remolque está empujando la troca en lugar de ayudar a frenar... ahí hay un problema serio.
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">4. Luces y Cableado — Simples pero Críticos</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Te sorprendería cuántos problemas vienen de un mal cableado. Revisa:
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Luces de freno</li>
            <li>• Direccionales</li>
            <li>• Luces de marcha</li>
            <li>• Cables expuestos o dañados</li>
          </ul>
          <p className="mt-6 text-muted-foreground">
            No solo es cuestión de seguridad — también es una multa esperando a pasar si no lo tienes en
            regla.
          </p>
        </div>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">5. Revisa el Chasis y las Soldaduras</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Tu remolque aguanta mucho castigo. Revisa:
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Los rieles del chasis (frame rails)</li>
            <li>• Los puntos de soldadura</li>
            <li>• Los travesaños (crossmembers)</li>
            <li>• El área de la lengüeta (tongue)</li>
          </ul>
          <p className="mt-6 text-muted-foreground">
            Las grietas pequeñas se vuelven problemas grandes si las ignoras.
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">6. Mantén Todo Engrasado</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Donde hay movimiento, debe haber grasa. Eso incluye:
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• El gato (jack)</li>
            <li>• El acoplador (coupler)</li>
            <li>• Las bisagras (compuertas, rampas, cama basculante)</li>
            <li>• Los componentes de la suspensión</li>
          </ul>
          <p className="mt-6 font-semibold text-foreground">Metal seco se traduce en desgaste, y desgaste se traduce en falla.</p>
        </div>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">7. Cuida tu Carga (Payload) — Esta es Grande</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Mucho del daño viene de una sola cosa: sobrecargar el remolque. Aunque "aguante" la carga,
            estás:
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Forzando el chasis</li>
            <li>• Desgastando los ejes más rápido</li>
            <li>• Quemando llantas y rodamientos antes de tiempo</li>
          </ul>
          <p className="mt-6 text-muted-foreground">Conoce tus límites y mantente dentro de ellos.</p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">8. Lávalo (Sí, Importa)</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            El lodo, la sal y el escombro van desgastando tu remolque con el tiempo.
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Enjuágalo con regularidad</li>
            <li>• Sobre todo después de acarrear tierra, piedra, o en clima húmedo</li>
          </ul>
          <p className="mt-6 text-muted-foreground">La pintura dura más... el remolque dura más.</p>
        </div>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">El Costo Real de Saltarte el Mantenimiento</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Saltarte el mantenimiento no ahorra dinero — solo atrasa el gasto y lo hace más grande.
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• $50–$100 en mantenimiento puede evitarte una reparación de $1,000 o más</li>
            <li>• 10 minutos de revisión pueden evitarte horas varado en el camino</li>
          </ul>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">En Resumen</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Si tu remolque te está haciendo ganar dinero... trátalo como tal. Mantente al tanto de las
            cosas pequeñas para que no se conviertan en problemas grandes.
          </p>
        </div>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">Sigue Leyendo</h2>
          <div className="mt-6 text-primary font-semibold space-y-2">
            <Link to="/es/costos-de-tener-un-remolque" className="block hover:underline">→ Costos Reales de Tener un Remolque</Link>
            <Link to="/es/remolques-de-volteo" className="block hover:underline">→ Tráileres de Volteo Explicadas</Link>
            <Link to="/es/remolque-volteo-14k-vs-16k" className="block hover:underline">→ Tráiler de Volteo 14K vs 16K</Link>
            <Link to="/es/gvwr-capacidad-de-carga" className="block hover:underline">→ GVWR y Capacidad de Carga</Link>
          </div>
        </div>
      </section>

      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl md:text-5xl font-black uppercase">¿Quieres Seguir Aprendiendo?</h2>
          <p className="mt-6 text-lg text-zinc-300">
            Explora más guías de mantenimiento y propiedad para que tu remolque dure más y evites
            reparaciones costosas.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/es/costos-de-tener-un-remolque" className="bg-red-700 px-6 py-3 rounded-md font-bold uppercase hover:bg-red-800">
              Costos de Tener un Remolque
            </Link>
            <Link to="/maintenance-ownership" className="border border-white/30 px-6 py-3 rounded-md font-bold uppercase hover:bg-white/10">
              Más Guías (en inglés)
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
